import { useCallback, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { genderOptions, genderOptionsWithNeutral } from '@common/constants';
import { useTranslations } from '@lang';
import { femaleTitlesOptions, maleTitlesOptions } from '@utils/guestTitles';
import { ConfigurationGroupType } from 'shared-types/SpaPackage';
import type { GuestConfig, OptionsPanelConfig } from 'voyager';

import { SpaPackageFormValues } from '../../SpaPackageForm.schema';
import { mapConfigGroupToOptionsPanel } from '../SpaDayBooking.helpers';

type UseGuestConfigParams = {
  form: UseFormReturn<SpaPackageFormValues>;
  watchedGuests: SpaPackageFormValues['guests'] | undefined;
  configGroupsNoExtras: ConfigurationGroupType[];
  showGenderNeutral: boolean;
};

export function useGuestConfig({
  form,
  watchedGuests,
  configGroupsNoExtras,
  showGenderNeutral,
}: UseGuestConfigParams) {
  const { t } = useTranslations();

  const titleOptions = useMemo(
    () => [...maleTitlesOptions, ...femaleTitlesOptions].map((o) => t(o.label)),
    [t]
  );

  const genderOpts = useMemo(() => {
    const opts = showGenderNeutral ? genderOptionsWithNeutral : genderOptions;
    return opts.map((o) => ({ value: o.value, label: t(o.id) }));
  }, [showGenderNeutral, t]);

  const titleValueToLabel = useMemo(() => {
    const map: Record<string, string> = {};
    [...maleTitlesOptions, ...femaleTitlesOptions].forEach((o) => {
      map[o.value] = t(o.label);
    });
    return map;
  }, [t]);

  const titleLabelToValue = useMemo(() => {
    const map: Record<string, string> = {};
    [...maleTitlesOptions, ...femaleTitlesOptions].forEach((o) => {
      map[t(o.label)] = o.value;
    });
    return map;
  }, [t]);

  const guests: GuestConfig[] = useMemo(() => {
    return (watchedGuests || []).map((guest, idx) => {
      const guestConfigGroups = guest.configurationGroups;
      const optionsPanels: OptionsPanelConfig[] = configGroupsNoExtras
        .filter((cg) => cg.isRequired)
        .map((cg) => mapConfigGroupToOptionsPanel(cg, guestConfigGroups))
        .filter((p): p is OptionsPanelConfig => p !== null);

      const guestFormValues = {
        title: titleValueToLabel[guest.title] || guest.title || '',
        firstName: guest.firstName || '',
        lastName: guest.lastName || '',
        gender: guest.gender || '',
      };

      return {
        guestNumber: idx + 1,
        values: guestFormValues,
        optionsPanels,
      };
    });
  }, [watchedGuests, configGroupsNoExtras, titleValueToLabel]);

  const handleGuestCountChange = useCallback(
    (count: number) => {
      const current = form.getValues('guests') || [];
      let nextGuests = current;
      if (count > current.length) {
        nextGuests = [
          ...current,
          ...Array.from({ length: count - current.length }, () => ({
            title: '',
            firstName: '',
            lastName: '',
            gender: '',
          })),
        ];
      } else if (count < current.length) {
        nextGuests = current.slice(0, count);
      }
      form.setValue('guests', nextGuests as SpaPackageFormValues['guests'], {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form]
  );

  const handleGuestChange = useCallback(
    (guestIndex: number, field: string, value: string) => {
      const currentGuests = form.getValues('guests') || [];
      const updatedGuests = currentGuests.map((guest, idx) => {
        if (idx !== guestIndex) return guest;
        if (field === 'title') {
          const actualValue = titleLabelToValue[value] || value;
          return { ...guest, title: actualValue };
        }
        return { ...guest, [field]: value };
      });
      form.setValue('guests', updatedGuests as SpaPackageFormValues['guests'], {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [form, titleLabelToValue]
  );

  const handleExistingGuestSelect = useCallback(
    (_guestIndex: number, _guestId: string | undefined) => {},
    []
  );

  return {
    titleOptions,
    genderOpts,
    guests,
    handleGuestCountChange,
    handleGuestChange,
    handleExistingGuestSelect,
  };
}
